import {
    type Column,
    type ColumnDef,
    type Row,
    type RowData,
    columnFilteringFeature,
    columnGroupingFeature,
    columnOrderingFeature,
    columnPinningFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createExpandedRowModel,
    createFilteredRowModel,
    createGroupedRowModel,
    createPaginatedRowModel,
    createSortedRowModel,
    globalFilteringFeature,
    metaHelper,
    rowAggregationFeature,
    rowExpandingFeature,
    rowPaginationFeature,
    rowPinningFeature,
    rowSelectionFeature,
    rowSortingFeature,
    sortFn_alphanumeric,
    sortFn_datetime,
    sortFn_text,
    tableFeatures,
} from '@tanstack/react-table';
import type { IDataTableColumn } from './DataTable.types';
import { dataTableAggregationFns } from './dataTableAggregates';

/** Мета колонки TanStack: публичная модель колонки атома — filterFn, рендер и
 *  раскладка узнают dataType и настройки без параллельных словарей. */
interface IDataTableColumnMeta {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- тип строки знает только сам DataTable<T>
    dataColumn?: IDataTableColumn<any>;
}

/**
 * Состав таблицы. Фичи и row-модели фиксируются при создании таблицы, поэтому
 * здесь всё, что атом может включить хоть в каком-то режиме; выключаются они
 * флагами `manual*` в опциях конкретного рендера.
 *
 * Функции сортировки — ровно те, что выбирает `sortFn: 'auto'` (`basic` у
 * библиотеки запасной и в реестре не нужен). Фильтры колонок и глобальный поиск
 * передаются функциями прямо в опции — реестр фильтров не нужен. Агрегаты — свои
 * (`dataTableAggregates`): те же определения считают итоги полноэкранного режима.
 */
export const dataTableFeatures = tableFeatures({
    columnVisibilityFeature,
    columnOrderingFeature,
    columnPinningFeature,
    columnSizingFeature,
    columnResizingFeature,
    columnFilteringFeature,
    globalFilteringFeature,
    rowSortingFeature,
    columnGroupingFeature,
    rowAggregationFeature,
    rowExpandingFeature,
    rowPaginationFeature,
    rowSelectionFeature,
    rowPinningFeature,
    filteredRowModel: createFilteredRowModel(),
    sortedRowModel: createSortedRowModel(),
    groupedRowModel: createGroupedRowModel(),
    expandedRowModel: createExpandedRowModel(),
    paginatedRowModel: createPaginatedRowModel(),
    sortFns: { alphanumeric: sortFn_alphanumeric, text: sortFn_text, datetime: sortFn_datetime },
    aggregationFns: dataTableAggregationFns,
    columnMeta: metaHelper<IDataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;
export type DataTableRow<T extends RowData> = Row<DataTableFeatures, T>;
export type DataTableColumn<T extends RowData> = Column<DataTableFeatures, T, unknown>;
export type DataTableColumnDef<T extends RowData> = ColumnDef<DataTableFeatures, T, unknown>;
